import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { isValidDiceExpression, getSpells, parseSpellDamage, parseSpellHealing, getCommand } from '@machina/core';
import type { CampaignNpc } from '@machina/core';
import { useDiceStore } from '../stores/diceStore';
import { useChatStore, ChatMessage, parseRollLabel, ROLL_LABELS, RollLabel, type SavePrompt } from '../stores/chatStore';
import { useSoloGamemasterStore } from '../stores/soloGamemasterStore';
import { useCampaignStore } from '../stores/campaignStore';
import { useSessionStore } from '../stores/sessionStore';
import { useInitiativeStore } from '../stores/initiativeStore';
import { useMobStore } from '../stores/mobStore';
import { useCharacterStore } from '../stores/characterStore';
import { useNpcStore } from '../stores/npcStore';
import { useCampaignMapStore } from '../stores/campaignMapStore';
import { useCombatStore } from '../stores/combatStore';
import { useEncounterStore } from '../stores/encounterStore';
import { useIsGM } from '../hooks/useIsGM';
import { DiceResultDisplay } from './DiceResultDisplay';
import { SpellCardFull } from './SpellCard';
import { Icon } from './Icon';
import { CharacterCreationWizard } from './CharacterCreationWizard';
import { useIsoSpriteStore, ALL_FACINGS } from '../stores/isoSpriteStore';
import { useEncounterLibraryStore } from '../stores/encounterLibraryStore';
import { buildMobSpriteParams } from '../lib/mobSpritePrompt';

/**
 * Lightweight inline markdown: **bold**, _italic_, and newlines.
 */
function formatChatText(text: string): React.ReactNode {
  const lines = text.split('\n');
  return lines.map((line, li) => {
    // Split on **bold** and _italic_ markers
    const parts: React.ReactNode[] = [];
    const re = /(\*\*(.+?)\*\*)|(_(.+?)_)/g;
    let last = 0;
    let match: RegExpExecArray | null;
    let ki = 0;
    while ((match = re.exec(line)) !== null) {
      if (match.index > last) {
        parts.push(line.slice(last, match.index));
      }
      if (match[2]) {
        parts.push(<strong key={ki++}>{match[2]}</strong>);
      } else if (match[4]) {
        parts.push(<em key={ki++}>{match[4]}</em>);
      }
      last = re.lastIndex;
    }
    if (last < line.length) parts.push(line.slice(last));

    return (
      <span key={li}>
        {parts}
        {li < lines.length - 1 && <br />}
      </span>
    );
  });
}

// Pre-built test encounters with basic 5e stats
interface TestMob {
  name: string;
  ac: number;
  maxHp: number;
  str: number;
  dex: number;
  con: number;
  int: number;
  wis: number;
  cha: number;
  actions: { name: string; attackBonus: number; damageDice: string; damageType: string }[];
}

const TEST_ENCOUNTERS: Record<string, { name: string; mobs: TestMob[] }> = {
  goblin: {
    name: 'Goblin Ambush',
    mobs: [
      { name: 'Goblin', ac: 15, maxHp: 7, str: 8, dex: 14, con: 10, int: 10, wis: 8, cha: 8, actions: [{ name: 'Scimitar', attackBonus: 4, damageDice: '1d6+2', damageType: 'slashing' }] },
      { name: 'Goblin', ac: 15, maxHp: 7, str: 8, dex: 14, con: 10, int: 10, wis: 8, cha: 8, actions: [{ name: 'Shortbow', attackBonus: 4, damageDice: '1d6+2', damageType: 'piercing' }] },
      { name: 'Goblin Boss', ac: 17, maxHp: 21, str: 10, dex: 14, con: 10, int: 10, wis: 8, cha: 10, actions: [{ name: 'Scimitar', attackBonus: 4, damageDice: '1d6+2', damageType: 'slashing' }] },
    ],
  },
  wolves: {
    name: 'Wolf Pack',
    mobs: [
      { name: 'Dire Wolf', ac: 14, maxHp: 37, str: 17, dex: 15, con: 15, int: 3, wis: 12, cha: 7, actions: [{ name: 'Bite', attackBonus: 5, damageDice: '2d6+3', damageType: 'piercing' }] },
      { name: 'Wolf', ac: 13, maxHp: 11, str: 12, dex: 15, con: 12, int: 3, wis: 12, cha: 6, actions: [{ name: 'Bite', attackBonus: 4, damageDice: '2d4+2', damageType: 'piercing' }] },
      { name: 'Wolf', ac: 13, maxHp: 11, str: 12, dex: 15, con: 12, int: 3, wis: 12, cha: 6, actions: [{ name: 'Bite', attackBonus: 4, damageDice: '2d4+2', damageType: 'piercing' }] },
    ],
  },
  skeleton: {
    name: 'Crypt Defenders',
    mobs: [
      { name: 'Skeleton', ac: 13, maxHp: 13, str: 10, dex: 14, con: 15, int: 6, wis: 8, cha: 5, actions: [{ name: 'Shortsword', attackBonus: 4, damageDice: '1d6+2', damageType: 'piercing' }] },
      { name: 'Skeleton', ac: 13, maxHp: 13, str: 10, dex: 14, con: 15, int: 6, wis: 8, cha: 5, actions: [{ name: 'Shortbow', attackBonus: 4, damageDice: '1d6+2', damageType: 'piercing' }] },
      { name: 'Skeleton', ac: 13, maxHp: 13, str: 10, dex: 14, con: 15, int: 6, wis: 8, cha: 5, actions: [{ name: 'Shortsword', attackBonus: 4, damageDice: '1d6+2', damageType: 'piercing' }] },
      { name: 'Skeleton', ac: 13, maxHp: 13, str: 10, dex: 14, con: 15, int: 6, wis: 8, cha: 5, actions: [{ name: 'Shortbow', attackBonus: 4, damageDice: '1d6+2', damageType: 'piercing' }] },
    ],
  },
  bandit: {
    name: 'Highway Robbery',
    mobs: [
      { name: 'Bandit Captain', ac: 15, maxHp: 65, str: 15, dex: 16, con: 14, int: 14, wis: 11, cha: 14, actions: [{ name: 'Scimitar', attackBonus: 5, damageDice: '1d6+3', damageType: 'slashing' }] },
      { name: 'Bandit', ac: 12, maxHp: 11, str: 11, dex: 12, con: 12, int: 10, wis: 10, cha: 10, actions: [{ name: 'Scimitar', attackBonus: 3, damageDice: '1d6+1', damageType: 'slashing' }] },
      { name: 'Bandit', ac: 12, maxHp: 11, str: 11, dex: 12, con: 12, int: 10, wis: 10, cha: 10, actions: [{ name: 'Light Crossbow', attackBonus: 3, damageDice: '1d8+1', damageType: 'piercing' }] },
    ],
  },
};

// =============================================================================
// NPC MESSAGE COMPONENT
// =============================================================================

function NpcMessageBubble({ msg }: { msg: ChatMessage }) {
  const borderColor = msg.factionColor || '#c0c0c0';

  return (
    <div
      style={{
        borderLeft: `3px solid ${borderColor}`,
        paddingLeft: 10,
        marginTop: 2,
      }}
    >
      <div style={{
        fontSize: 11,
        fontFamily: 'Georgia, serif',
        fontStyle: 'italic',
        color: '#d0c8d8',
        lineHeight: 1.5,
      }}>
        "{msg.message}"
      </div>
    </div>
  );
}

/**
 * AI DM narration bubble — gold left border, Georgia serif, subtle wand icon.
 * Also renders choice buttons if the message has choiceButtons attached.
 */
function AiDmMessageBubble({ msg, onAction }: { msg: ChatMessage; onAction?: (action: string) => void }) {
  const [clicked, setClicked] = useState<string | null>(null);
  const buttons = msg.choiceButtons;

  return (
    <div
      style={{
        borderLeft: '3px solid #d4af37',
        paddingLeft: 10,
        marginTop: 2,
      }}
    >
      {msg.message && (
        <div style={{
          fontSize: 13,
          fontFamily: 'Georgia, serif',
          color: '#e8e0f0',
          lineHeight: 1.6,
        }}>
          {formatChatText(msg.message)}
        </div>
      )}
      {buttons && buttons.length > 0 && onAction && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {buttons.map((btn, i) => {
            const isClicked = clicked === btn.action;
            const isDisabled = clicked !== null;
            return (
              <button
                key={i}
                onClick={() => {
                  if (isDisabled) return;
                  setClicked(btn.action);
                  onAction(btn.action);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: 13,
                  fontFamily: 'Georgia, serif',
                  fontWeight: 600,
                  color: isClicked ? '#0a080c' : '#d4af37',
                  backgroundColor: isClicked ? '#d4af37' : 'transparent',
                  border: '1px solid #d4af37',
                  borderRadius: 6,
                  cursor: isDisabled ? 'default' : 'pointer',
                  opacity: isDisabled && !isClicked ? 0.4 : 1,
                  transition: 'all 0.2s ease',
                }}
              >
                {btn.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// 5e skill → ability mapping
const SKILL_ABILITY: Record<string, string> = {
  acrobatics: 'dexterity', 'animal handling': 'wisdom', arcana: 'intelligence',
  athletics: 'strength', deception: 'charisma', history: 'intelligence',
  insight: 'wisdom', intimidation: 'charisma', investigation: 'intelligence',
  medicine: 'wisdom', nature: 'intelligence', perception: 'wisdom',
  performance: 'charisma', persuasion: 'charisma', religion: 'intelligence',
  'sleight of hand': 'dexterity', stealth: 'dexterity', survival: 'wisdom',
};

/** Calculate roll modifier from character sheet for a given roll request */
function getRollModifier(rollData: NonNullable<ChatMessage['rollRequestData']>): number {
  const chars = useCharacterStore.getState().characters;
  // Find the player's (non-NPC) character
  const pc = Array.from(chars.values()).find((c) => !c.isNpc);
  if (!pc) return 0;

  const scores = pc.abilityScores;
  const profBonus = pc.proficiencyBonus ?? 2;

  // Resolve which ability to use
  let abilityName: string | undefined;
  if (rollData.rollType === 'skill_check' && rollData.skill) {
    abilityName = SKILL_ABILITY[rollData.skill.toLowerCase()];
  }
  if (!abilityName && rollData.ability) {
    abilityName = rollData.ability.toLowerCase();
  }

  // Map ability name to score
  const abilityKey = abilityName as keyof typeof scores | undefined;
  const score = abilityKey ? (scores[abilityKey] ?? 10) : 10;
  const abilityMod = Math.floor((score - 10) / 2);

  // Add proficiency if applicable
  if (rollData.rollType === 'saving_throw' && rollData.ability) {
    const saveName = rollData.ability.toLowerCase();
    if (pc.savingThrowProficiencies?.map((s: string) => s.toLowerCase()).includes(saveName)) {
      return abilityMod + profBonus;
    }
    return abilityMod;
  }

  if (rollData.rollType === 'skill_check' && rollData.skill) {
    const skillName = rollData.skill.toLowerCase();
    if (pc.skillExpertise?.map((s: string) => s.toLowerCase()).includes(skillName)) {
      return abilityMod + profBonus * 2;
    }
    if (pc.skillProficiencies?.map((s: string) => s.toLowerCase()).includes(skillName)) {
      return abilityMod + profBonus;
    }
    return abilityMod;
  }

  if (rollData.rollType === 'attack') {
    // Default to STR for melee, DEX for ranged — ability field tells us
    return abilityMod + profBonus;
  }

  return abilityMod;
}

/**
 * Roll request card — interactive button for the player to roll.
 */
function RollRequestCard({ msg }: { msg: ChatMessage }) {
  const rollData = msg.rollRequestData;
  if (!rollData) return null;

  const addDiceRoll = useChatStore((s) => s.addDiceRoll);
  const handleRollResult = useSoloGamemasterStore((s) => s.handleRollResult);
  const [rolled, setRolled] = useState(false);

  const skillOrAbility = rollData.skill || rollData.ability || '';
  const typeLabel = rollData.rollType === 'saving_throw' ? 'Saving Throw' :
    rollData.rollType === 'skill_check' ? 'Skill Check' :
    rollData.rollType === 'ability_check' ? 'Ability Check' :
    rollData.rollType === 'attack' ? 'Attack Roll' : 'Roll';

  const handleClick = () => {
    if (rolled) return;
    setRolled(true);

    // Roll 1d20 with proper modifier from character sheet
    const natural = Math.floor(Math.random() * 20) + 1;
    const modifier = getRollModifier(rollData);
    const total = natural + modifier;
    const notation = modifier >= 0 ? `1d20+${modifier}` : `1d20${modifier}`;

    addDiceRoll({
      sender: 'You',
      notation,
      result: {
        notation,
        total,
        rolls: [{ sides: 20, value: natural }],
      },
      rollLabel: rollData.rollType === 'saving_throw' ? 'save' :
        rollData.rollType === 'attack' ? 'attack' : 'check',
    });

    // Send to AI DM for adjudication
    handleRollResult(notation, total, natural, `${typeLabel}: ${skillOrAbility}`);
  };

  return (
    <div style={{
      border: '1px solid rgba(204, 93, 232, 0.4)',
      borderRadius: 6,
      padding: '8px 12px',
      marginTop: 4,
      background: 'rgba(204, 93, 232, 0.08)',
    }}>
      <div style={{
        fontSize: 11,
        color: '#cc5de8',
        fontWeight: 600,
        marginBottom: 4,
      }}>
        {typeLabel}{skillOrAbility ? `: ${skillOrAbility}` : ''}
      </div>
      <div style={{
        fontSize: 12,
        color: '#d0c8d8',
        marginBottom: 6,
      }}>
        {rollData.reason}
        {!rollData.hideDc && <span style={{ color: '#cc5de8', marginLeft: 8 }}>DC {rollData.dc}</span>}
      </div>
      <button
        onClick={handleClick}
        disabled={rolled || rollData.resolved}
        style={{
          padding: '4px 16px',
          borderRadius: 4,
          border: 'none',
          background: rolled ? 'rgba(204, 93, 232, 0.2)' : '#cc5de8',
          color: rolled ? '#999' : '#fff',
          fontSize: 12,
          fontWeight: 600,
          cursor: rolled ? 'default' : 'pointer',
        }}
      >
        {rolled ? 'Rolled' : 'Roll d20'}
      </button>
    </div>
  );
}

// NPC SPEAKER SELECTOR removed — NPC speaker is now set via npcStore.setSpeakingAs()
// from the Keystone NPC Panel's "Speak as" button. The chat input shows a dismiss
// indicator when an NPC is active (see the inline code in ChatWindow below).

// =============================================================================
// DIALOGUE HINT CHIPS
// =============================================================================

function DialogueHintChips({
  npc,
  onSelect,
}: {
  npc: CampaignNpc;
  onSelect: (hint: string) => void;
}) {
  if (!npc.dialogueHints || npc.dialogueHints.length === 0) return null;

  return (
    <div style={{
      display: 'flex',
      gap: 4,
      flexWrap: 'wrap',
      padding: '4px 0',
    }}>
      {npc.dialogueHints.map((hint, i) => (
        <button
          key={i}
          onClick={() => onSelect(hint)}
          title={hint}
          style={{
            padding: '3px 8px',
            backgroundColor: 'rgba(212, 175, 55, 0.08)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            borderRadius: 12,
            cursor: 'pointer',
            color: 'rgba(192, 192, 192, 0.7)',
            fontSize: 10,
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.15)';
            e.currentTarget.style.color = '#d4af37';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.08)';
            e.currentTarget.style.color = 'rgba(192, 192, 192, 0.7)';
          }}
        >
          "{hint.length > 30 ? hint.slice(0, 30) + '...' : hint}"
        </button>
      ))}
    </div>
  );
}

// Helper: get faction color by ID (duplicated from grist-lore to avoid heavy import)
function getFactionColorForChat(factionId: string): string {
  const colors: Record<string, string> = {
    iron_covenant: '#8B4513',
    verdance: '#228B22',
    witnesses: '#4169E1',
    free_drift: '#DAA520',
    final_threshold: '#2F4F4F',
    ascending_flame: '#FF6347',
    dissolvers: '#8B008B',
  };
  return colors[factionId] || 'rgba(192, 192, 192, 0.4)';
}

// =============================================================================
// SPELL DAMAGE BUTTON (shown below spell cards in chat)
// =============================================================================

function SpellDamageButton({ spellDamage, sender }: { spellDamage: { dice: string; damageType: string; spellName: string }; sender: string }) {
  const roll = useDiceStore((s) => s.roll);
  const isReady = useDiceStore((s) => s.isReady);
  const addDiceRoll = useChatStore((s) => s.addDiceRoll);
  const [rolled, setRolled] = useState(false);

  const handleRollDamage = async () => {
    if (!isReady || rolled) return;

    const result = await roll(spellDamage.dice);
    if (!result) return;

    addDiceRoll({
      sender,
      notation: `${spellDamage.dice} (${spellDamage.spellName} — ${spellDamage.damageType})`,
      result: {
        notation: `${spellDamage.dice} ${spellDamage.damageType} (${spellDamage.spellName})`,
        rolls: result.rolls,
        total: result.total,
      },
      rollLabel: 'damage',
    });

    // Forward spell damage to AI DM in solo mode
    const soloState = useSoloGamemasterStore.getState();
    if (soloState.isSoloMode) {
      soloState.handleRollResult(
        spellDamage.dice,
        result.total,
        result.rolls[0]?.value ?? result.total,
        `${spellDamage.spellName} damage (${spellDamage.damageType})`,
      );
    }

    setRolled(true);
  };

  return (
    <button
      onClick={handleRollDamage}
      disabled={!isReady || rolled}
      style={{
        marginTop: 6,
        padding: '5px 12px',
        background: rolled
          ? 'rgba(100, 100, 100, 0.3)'
          : 'linear-gradient(180deg, rgba(180, 80, 20, 0.5) 0%, rgba(120, 50, 10, 0.7) 100%)',
        border: `1px solid ${rolled ? 'rgba(100, 100, 100, 0.3)' : 'rgba(255, 150, 50, 0.5)'}`,
        borderRadius: 3,
        cursor: rolled || !isReady ? 'default' : 'pointer',
        color: rolled ? 'rgba(150, 150, 150, 0.6)' : 'rgba(255, 200, 150, 0.95)',
        fontSize: 11,
        fontFamily: 'var(--font-mono)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        opacity: rolled ? 0.6 : 1,
        transition: 'all 0.15s',
      }}
      title={rolled ? 'Damage already rolled' : `Roll ${spellDamage.dice} ${spellDamage.damageType} damage`}
    >
      <Icon name="dice" size={12} color={rolled ? 'rgba(150, 150, 150, 0.6)' : 'rgba(255, 200, 150, 0.95)'} />
      {rolled ? `${spellDamage.dice} ${spellDamage.damageType} rolled` : `Roll Damage — ${spellDamage.dice} ${spellDamage.damageType}`}
    </button>
  );
}

// =============================================================================
// SPELL HEALING BUTTON (shown below healing spell cards in chat)
// =============================================================================

function SpellHealingButton({ spellHealing, sender }: { spellHealing: { dice: string; spellName: string; abilityMod: number }; sender: string }) {
  const roll = useDiceStore((s) => s.roll);
  const isReady = useDiceStore((s) => s.isReady);
  const addDiceRoll = useChatStore((s) => s.addDiceRoll);
  const [rolled, setRolled] = useState(false);

  const modStr = spellHealing.abilityMod > 0 ? `+${spellHealing.abilityMod}` : spellHealing.abilityMod < 0 ? `${spellHealing.abilityMod}` : '';
  const fullNotation = modStr ? `${spellHealing.dice}${modStr}` : spellHealing.dice;
  const displayLabel = modStr ? `${spellHealing.dice} ${modStr}` : spellHealing.dice;

  const handleRollHealing = async () => {
    if (!isReady || rolled) return;

    const result = await roll(fullNotation);
    if (!result) return;

    addDiceRoll({
      sender,
      notation: `${fullNotation} (${spellHealing.spellName} — healing)`,
      result: {
        notation: `${fullNotation} healing (${spellHealing.spellName})`,
        rolls: result.rolls,
        total: result.total,
      },
      rollLabel: 'heal',
    });

    // Forward healing to AI DM in solo mode
    const soloState = useSoloGamemasterStore.getState();
    if (soloState.isSoloMode) {
      soloState.handleRollResult(
        fullNotation,
        result.total,
        result.rolls[0]?.value ?? result.total,
        `${spellHealing.spellName} healing`,
      );
    }

    setRolled(true);
  };

  return (
    <button
      onClick={handleRollHealing}
      disabled={!isReady || rolled}
      style={{
        marginTop: 6,
        padding: '5px 12px',
        background: rolled
          ? 'rgba(100, 100, 100, 0.3)'
          : 'linear-gradient(180deg, rgba(20, 120, 80, 0.5) 0%, rgba(10, 80, 50, 0.7) 100%)',
        border: `1px solid ${rolled ? 'rgba(100, 100, 100, 0.3)' : 'rgba(32, 201, 151, 0.5)'}`,
        borderRadius: 3,
        cursor: rolled || !isReady ? 'default' : 'pointer',
        color: rolled ? 'rgba(150, 150, 150, 0.6)' : 'rgba(150, 255, 200, 0.95)',
        fontSize: 11,
        fontFamily: 'var(--font-mono)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        opacity: rolled ? 0.6 : 1,
        transition: 'all 0.15s',
      }}
      title={rolled ? 'Healing already rolled' : `Roll ${displayLabel} healing`}
    >
      <Icon name="heart" size={12} color={rolled ? 'rgba(150, 150, 150, 0.6)' : 'rgba(150, 255, 200, 0.95)'} />
      {rolled ? `${displayLabel} healing rolled` : `Roll Healing — ${displayLabel}`}
    </button>
  );
}

// =============================================================================
// SAVE PROMPT CARD (shown in chat for save-based spells)
// =============================================================================

function IsoSpritePromptCard() {
  const characters = useCharacterStore((s) => s.characters);
  const mobs = useMobStore((s) => s.mobs);
  const generating = useIsoSpriteStore((s) => s.generating);
  const generateMissing = useIsoSpriteStore((s) => s.generateMissing);
  const facingCount = useIsoSpriteStore((s) => s.facingCount);
  const seeds = useIsoSpriteStore((s) => s.seeds);
  const setSeed = useIsoSpriteStore((s) => s.setSeed);
  const [done, setDone] = useState(false);

  // Build combined list — one entry per character + one per ALIVE mob instance.
  // Each entity has its own UUID-keyed 4-facing sprite set.
  const allTargets: Array<{ id: string; label: string; type: 'character' | 'mob' }> = [];

  for (const c of characters.values()) {
    allTargets.push({ id: c.id, label: c.name, type: 'character' });
  }
  for (const m of mobs.values()) {
    if (m.dead) continue;
    allTargets.push({ id: m.id, label: m.displayName || m.name, type: 'mob' });
  }

  const totalSprites = allTargets.length * 4;
  const existingSprites = allTargets.reduce((sum, t) => sum + facingCount(t.id), 0);
  const missingCount = totalSprites - existingSprites;
  const isAnyGenerating = generating.size > 0;
  const allReady = missingCount === 0 && allTargets.length > 0;

  useEffect(() => {
    if (missingCount > 0 && done) setDone(false);
  }, [missingCount, done]);

  const handleGenerate = async () => {
    const params: Parameters<typeof generateMissing>[0] = [];

    const deployment = useEncounterLibraryStore.getState().activeDeployment;
    const specByName = deployment?.mobSpecByBaseName ?? {};

    for (const target of allTargets) {
      if (facingCount(target.id) >= 4) continue;

      if (target.type === 'character') {
        const c = characters.get(target.id);
        if (c) {
          const mainClass = c.classes?.[0];
          params.push({
            id: c.id,
            name: c.name,
            race: c.race,
            className: mainClass?.name,
            subclass: mainClass?.subclass,
            equipment: c.equipment,
            personality: c.personalityTraits,
            appearance: c.appearance,
            isNpc: c.isNpc,
            // Auto-save to character row so other players pick up the sprites
            persistTarget: { kind: 'character', characterId: c.id },
          });

          if (!seeds[c.id]) {
            const seedUrl = c.avatarUrl || c.tokenImageUrl;
            if (seedUrl) setSeed(c.id, seedUrl);
          }
        }
      } else {
        const mob = mobs.get(target.id);
        if (mob) {
          const mobAny = mob as unknown as { bestiaryId?: string | null };
          const base = buildMobSpriteParams(mob, specByName[mob.name] ?? null);
          // Prefer bestiary template (shared). Fall back to per-instance mob row
          // for ad-hoc spawns so every mob's sprites still sync to all players.
          params.push({
            ...base,
            persistTarget: mobAny.bestiaryId
              ? { kind: 'bestiary', bestiaryId: mobAny.bestiaryId }
              : { kind: 'mob', mobId: mob.id },
          });
        }
      }
    }

    if (params.length > 0) {
      await generateMissing(params);
      setDone(true);
    }
  };

  const genLabel = generating.size > 0 ? Array.from(generating)[0]?.replace(':', ' ') : '';

  return (
    <div style={{
      backgroundColor: 'rgba(212, 175, 55, 0.08)',
      border: '1px solid rgba(212, 175, 55, 0.3)',
      borderRadius: 6,
      padding: '10px 12px',
      marginTop: 4,
    }}>
      <div style={{
        fontFamily: 'Georgia, serif',
        fontSize: 12,
        color: 'var(--color-text-secondary)',
        marginBottom: 8,
      }}>
        {allReady || done
          ? `All ${allTargets.length} entities ready (4 directions each). Click any pill to re-roll that one.`
          : `${missingCount} directional sprites needed (${allTargets.length} entities \u00d7 4). Click any pill to generate just that entity, or the button for all.`}
      </div>

      {/* Character + mob list with facing progress */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        {allTargets.map((t) => {
          const count = facingCount(t.id);
          const isGen = useIsoSpriteStore.getState().isGenerating(t.id);
          const isComplete = count >= 4;
          const hasSeed = !!seeds[t.id];

          const reRoll = () => {
            // Clear this entity's 4 facings, then regen
            useIsoSpriteStore.setState((s) => {
              const newSprites = { ...s.sprites };
              for (const f of ALL_FACINGS) {
                delete newSprites[`${t.id}:${f}`];
              }
              try { localStorage.setItem('machina:iso-sprites', JSON.stringify(newSprites)); } catch {}
              return { sprites: newSprites };
            });
            if (t.type === 'character') {
              const c = characters.get(t.id);
              if (c) {
                const mainClass = c.classes?.[0];
                generateMissing([{
                  id: c.id, name: c.name, race: c.race, className: mainClass?.name,
                  subclass: mainClass?.subclass, equipment: c.equipment,
                  personality: c.personalityTraits, appearance: c.appearance, isNpc: c.isNpc,
                  persistTarget: { kind: 'character', characterId: c.id },
                }]);
              }
            } else {
              const mob = mobs.get(t.id);
              if (mob) {
                const deployment = useEncounterLibraryStore.getState().activeDeployment;
                const specHint = deployment?.mobSpecByBaseName?.[mob.name] ?? null;
                const base = buildMobSpriteParams(mob, specHint);
                const mobAny = mob as unknown as { bestiaryId?: string | null };
                generateMissing([{
                  ...base,
                  persistTarget: mobAny.bestiaryId
                    ? { kind: 'bestiary', bestiaryId: mobAny.bestiaryId }
                    : { kind: 'mob', mobId: mob.id },
                }]);
              }
            }
          };

          // Clicking any pill (complete or not) triggers per-entity generation:
          // - incomplete → gen just the missing facings for this one
          // - complete → reroll all 4 from scratch
          const clickHandler = isGen ? undefined : reRoll;

          return (
            <span
              key={t.id}
              onClick={clickHandler}
              title={isComplete
                ? `Click to re-roll all 4 facings for ${t.label}${hasSeed ? ' (has seed)' : ''}`
                : `Click to generate ${4 - count} missing facing${4 - count !== 1 ? 's' : ''} for ${t.label}${hasSeed ? ' \u2022 seed set' : ''}`}
              style={{
                fontSize: 11,
                fontFamily: 'Georgia, serif',
                padding: '2px 8px',
                borderRadius: 3,
                backgroundColor: isComplete ? 'rgba(76, 175, 80, 0.15)' : isGen ? 'rgba(212, 175, 55, 0.15)' : 'rgba(255,255,255,0.05)',
                color: isComplete ? '#4caf50' : isGen ? '#d4af37' : 'var(--color-text-secondary)',
                border: `1px solid ${isComplete ? 'rgba(76, 175, 80, 0.3)' : isGen ? 'rgba(212, 175, 55, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                cursor: isGen ? 'wait' : 'pointer',
              }}
            >
              {isGen ? '...' : isComplete ? '\u21BB' : `${count}/4`}{hasSeed ? '\u2009\u2740' : ''} {t.label}{t.type === 'mob' ? ' (mob)' : ''}
            </span>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 6 }}>
        {!allReady && !done ? (
          <button
            onClick={handleGenerate}
            disabled={isAnyGenerating}
            style={{
              flex: 1,
              padding: '6px 12px',
              borderRadius: 4,
              border: '1px solid #d4af37',
              backgroundColor: isAnyGenerating ? 'rgba(212, 175, 55, 0.1)' : 'rgba(212, 175, 55, 0.2)',
              color: '#d4af37',
              cursor: isAnyGenerating ? 'wait' : 'pointer',
              fontFamily: 'Georgia, serif',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <Icon name={isAnyGenerating ? 'loader' : 'sparkle'} size={14} />
            {isAnyGenerating
              ? `Rendering ${genLabel}...`
              : `Generate ${missingCount} Sprite${missingCount !== 1 ? 's' : ''} (4 per character)`}
          </button>
        ) : null}

        {/* Regenerate mobs button — chars persist across scenes so we never wipe them here */}
        <button
          onClick={() => {
            // Clear ONLY mob sprites. Characters are long-lived; players spend time
            // on their look and shouldn't lose it to an accidental click.
            const mobIds = new Set<string>();
            for (const m of mobs.values()) mobIds.add(m.id);
            useIsoSpriteStore.setState((s) => {
              const newSprites = { ...s.sprites };
              for (const id of mobIds) {
                for (const f of ALL_FACINGS) {
                  delete newSprites[`${id}:${f}`];
                }
              }
              try { localStorage.setItem('machina:iso-sprites', JSON.stringify(newSprites)); } catch {}
              return { sprites: newSprites };
            });
            setDone(false);
          }}
          disabled={isAnyGenerating}
          title="Clear mob sprites only (keeps characters)"
          style={{
            padding: '6px 12px',
            borderRadius: 4,
            border: '1px solid rgba(255,255,255,0.15)',
            backgroundColor: 'rgba(255,255,255,0.05)',
            color: 'var(--color-text-secondary)',
            cursor: isAnyGenerating ? 'not-allowed' : 'pointer',
            fontFamily: 'Georgia, serif',
            fontSize: 11,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Icon name="refresh-cw" size={12} />
          Regen Mobs
        </button>
      </div>
    </div>
  );
}

// =============================================================================

function SavePromptCard({ savePrompt }: { savePrompt: SavePrompt }) {
  const isGM = useIsGM();
  const roll = useDiceStore((s) => s.roll);
  const isReady = useDiceStore((s) => s.isReady);
  const addDiceRoll = useChatStore((s) => s.addDiceRoll);
  // Pull targets from initiative (mob entries in the fight) + mob store for save bonuses
  const initEntries = useInitiativeStore((s) => s.entries);
  const mobs = useMobStore((s) => s.mobs);
  const [rolledFor, setRolledFor] = useState<Set<string>>(new Set());

  const abilityShort = savePrompt.ability.slice(0, 3).toUpperCase();
  // Targets = alive mobs from initiative + any alive mobs on the board not yet in initiative
  const mobTargets = isGM
    ? (() => {
        const initMobIds = new Set<string>();
        const targets: Array<{ id: string; name: string; type: 'mob' }> = [];
        // First: mobs in initiative that aren't dead
        for (const e of initEntries) {
          if (e.type !== 'mob') continue;
          const mob = mobs.get(e.id);
          if (mob?.dead) continue;
          initMobIds.add(e.id);
          targets.push({ id: e.id, name: e.name, type: 'mob' });
        }
        // Second: alive mobs on the board but not in initiative
        for (const [id, mob] of mobs) {
          if (mob.dead || initMobIds.has(id)) continue;
          targets.push({ id, name: mob.name, type: 'mob' });
        }
        return targets;
      })()
    : [];

  const handleRollSave = async (entryId: string, entryName: string) => {
    if (!isReady) return;

    const mob = useMobStore.getState().mobs.get(entryId);
    // Calculate save bonus: mob store data if available, otherwise +0
    let saveBonus = 0;
    if (mob) {
      const ability = savePrompt.ability as keyof typeof mob.saves;
      const abilityScore = mob[ability as keyof Pick<typeof mob, 'strength' | 'dexterity' | 'constitution' | 'intelligence' | 'wisdom' | 'charisma'>] as number;
      saveBonus = mob.saves[ability] ?? Math.floor((abilityScore - 10) / 2);
    }
    const bonusStr = saveBonus >= 0 ? `+${saveBonus}` : `${saveBonus}`;
    const notation = `1d20${bonusStr}`;

    const result = await roll(notation);
    if (!result) return;

    const passed = result.total >= savePrompt.dc;

    addDiceRoll({
      sender: 'GM',
      characterName: entryName,
      notation: `${notation} (${abilityShort} Save vs ${savePrompt.spellName})`,
      result: {
        notation: `${notation} (${abilityShort} Save${passed ? ' - PASS' : ' - FAIL'})`,
        rolls: result.rolls,
        total: result.total,
      },
      rollLabel: 'save',
    });

    setRolledFor((prev) => new Set(prev).add(entryId));
  };

  return (
    <div style={{
      marginTop: 4,
      padding: '8px 12px',
      background: 'linear-gradient(180deg, rgba(180, 140, 20, 0.15) 0%, rgba(120, 90, 10, 0.1) 100%)',
      border: '1px solid rgba(212, 175, 55, 0.3)',
      borderRadius: 4,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: isGM && mobTargets.length > 0 ? 8 : 0,
      }}>
        <Icon name="shield" size={14} color="#ffd700" />
        <span style={{
          fontSize: 12,
          fontFamily: 'Georgia, serif',
          color: '#ffd700',
          fontWeight: 600,
        }}>
          {savePrompt.spellName}
        </span>
        <span style={{
          fontSize: 11,
          color: 'rgba(212, 175, 55, 0.8)',
        }}>
          — {abilityShort} Save DC {savePrompt.dc}
        </span>
      </div>
      {isGM && mobTargets.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {mobTargets.map((entry) => {
            const alreadyRolled = rolledFor.has(entry.id);
            return (
              <button
                key={entry.id}
                onClick={() => handleRollSave(entry.id, entry.name)}
                disabled={!isReady || alreadyRolled}
                style={{
                  padding: '3px 8px',
                  background: alreadyRolled
                    ? 'rgba(100, 100, 100, 0.3)'
                    : 'rgba(212, 175, 55, 0.15)',
                  border: `1px solid ${alreadyRolled ? 'rgba(100, 100, 100, 0.3)' : 'rgba(212, 175, 55, 0.4)'}`,
                  borderRadius: 3,
                  fontSize: 10,
                  cursor: alreadyRolled || !isReady ? 'default' : 'pointer',
                  color: alreadyRolled ? 'rgba(150, 150, 150, 0.6)' : '#ffd700',
                  fontFamily: 'Georgia, serif',
                  transition: 'all 0.15s',
                  opacity: alreadyRolled ? 0.6 : 1,
                }}
                title={alreadyRolled ? `${entry.name} already rolled` : `Roll ${abilityShort} save for ${entry.name}`}
              >
                {alreadyRolled ? `${entry.name} ✓` : entry.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// CHAT WINDOW
// =============================================================================

// Static styles hoisted to module level — avoids re-allocation on every render
const chatStyles = {
  wrapper: { display: 'flex', flexDirection: 'column', height: '100%' } as const,
  messageList: {
    flex: 1, overflowY: 'auto', padding: 'var(--spacing-md)',
    fontSize: 'var(--font-size-sm)', lineHeight: 'var(--line-height-relaxed)',
  } as const,
  messageBubble: {
    marginBottom: 'var(--spacing-md)', paddingBottom: 'var(--spacing-sm)',
    borderBottom: 'var(--border-width-thin) solid var(--color-border)',
  } as const,
  senderRow: {
    display: 'flex', alignItems: 'baseline',
    gap: 'var(--spacing-xs)', marginBottom: 'var(--spacing-xs)',
  } as const,
  timestamp: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' } as const,
  npcSender: {
    fontWeight: 600, fontFamily: 'Georgia, serif',
    display: 'flex', alignItems: 'baseline', gap: 4,
  } as const,
  npcTitle: {
    fontWeight: 400, fontSize: 'var(--font-size-xs)',
    color: 'rgba(192, 192, 192, 0.5)', fontStyle: 'italic',
  } as const,
  aiDmSender: {
    fontWeight: 600, fontFamily: 'Georgia, serif', color: '#d4af37',
    display: 'flex', alignItems: 'center', gap: 4,
  } as const,
  spellSection: { marginTop: 'var(--spacing-xs)' } as const,
  inputArea: {
    padding: 'var(--spacing-md)',
    borderTop: 'var(--border-width-thin) solid var(--color-border)',
    backgroundColor: 'var(--color-background-elevated)',
  } as const,
  inputRow: { display: 'flex', gap: 6, alignItems: 'center' } as const,
  loadingWave: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '100%', gap: 16, opacity: 0.7,
  } as const,
  loadingText: {
    fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 13,
    color: 'var(--color-text-muted)', letterSpacing: '0.05em',
  } as const,
  aiThinking: {
    padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8,
    color: '#9333ea', fontSize: 'var(--font-size-sm)',
    fontFamily: 'Georgia, serif', fontStyle: 'italic', opacity: 0.8,
  } as const,
  aiError: {
    padding: '6px 12px', margin: '4px 8px',
    backgroundColor: 'rgba(220, 20, 60, 0.1)', border: '1px solid rgba(220, 20, 60, 0.3)',
    borderRadius: 4, color: '#dc143c', fontSize: 'var(--font-size-xs)',
  } as const,
};

interface ChatWindowProps {
  playerName?: string;
}

/**
 * Chat window for team communication
 * Uses shared chatStore so dice rolls from anywhere appear here
 */
export function ChatWindow({ playerName = 'Player' }: ChatWindowProps) {
  const roll = useDiceStore((s) => s.roll);
  const isReady = useDiceStore((s) => s.isReady);
  const messages = useChatStore((s) => s.messages);
  const isChatLoading = useChatStore((s) => s.isLoading);
  const addMessage = useChatStore((s) => s.addMessage);
  const addDiceRoll = useChatStore((s) => s.addDiceRoll);
  const addSpellLookup = useChatStore((s) => s.addSpellLookup);
  const addNpcMessage = useChatStore((s) => s.addNpcMessage);
  const addSystemMessage = useChatStore((s) => s.addSystemMessage);
  const saveMessageToSupabase = useChatStore((s) => s.saveMessageToSupabase);
  const currentCampaignId = useCampaignStore((s) => s.currentCampaignId);
  const userId = useSessionStore((s) => s.userId);
  const isGM = useIsGM();

  // Resolve the player's active character name for chat sender
  const characters = useCharacterStore((s) => s.characters);
  const characterOwners = useCharacterStore((s) => s.characterOwners);
  const partyIds = useCharacterStore((s) => s.partyIds);
  // Filter to characters owned by this user (not other players' characters)
  const myCharacter = useMemo(() =>
    partyIds
      .filter((id) => !userId || characterOwners.get(id) === userId)
      .map((id) => characters.get(id))
      .find((c) => c && !c.isNpc),
    [partyIds, userId, characterOwners, characters]
  );
  // GMs post under their own name (not any character they happen to own).
  // Players post as their character when possible, else their username.
  const chatSender = isGM ? playerName : (myCharacter?.name || playerName);
  const chatCharacterName = isGM ? undefined : myCharacter?.name;
  const clearInitiative = useInitiativeStore((s) => s.clearAllEntries);
  const removeAllMobs = useMobStore((s) => s.removeAllMobs);
  const [inputValue, setInputValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [tempInput, setTempInput] = useState(''); // Store current input when browsing history
  // NPC speaker state — shared via npcStore so GM Tray / token clicks can set it
  const speakingAsNpc = useNpcStore((s) => s.speakingAs);
  const setSpeakingAsNpc = useNpcStore((s) => s.setSpeakingAs);
  // Solo mode state
  const isSoloMode = useSoloGamemasterStore((s) => s.isSoloMode);
  const isAiProcessing = useSoloGamemasterStore((s) => s.isProcessing);
  const aiError = useSoloGamemasterStore((s) => s.error);
  const [showCharCreation, setShowCharCreation] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Helper to add message and sync to Supabase
  const addAndSyncMessage = useCallback(async (msg: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    addMessage(msg);
    const msgs = useChatStore.getState().messages;
    const newMsg = msgs[msgs.length - 1];
    if (currentCampaignId && newMsg) {
      saveMessageToSupabase(newMsg, currentCampaignId, userId || undefined);
    }
  }, [addMessage, currentCampaignId, userId, saveMessageToSupabase]);

  // Helper for dice rolls
  const addAndSyncDiceRoll = useCallback(async (params: Parameters<typeof addDiceRoll>[0]) => {
    addDiceRoll(params);
    const msgs = useChatStore.getState().messages;
    const newMsg = msgs[msgs.length - 1];
    if (currentCampaignId && newMsg) {
      saveMessageToSupabase(newMsg, currentCampaignId, userId || undefined);
    }
  }, [addDiceRoll, currentCampaignId, userId, saveMessageToSupabase]);

  // Helper for spell lookups
  const addAndSyncSpellLookup = useCallback(async (params: Parameters<typeof addSpellLookup>[0]) => {
    addSpellLookup(params);
    const msgs = useChatStore.getState().messages;
    const newMsg = msgs[msgs.length - 1];
    if (currentCampaignId && newMsg) {
      saveMessageToSupabase(newMsg, currentCampaignId, userId || undefined);
    }
  }, [addSpellLookup, currentCampaignId, userId, saveMessageToSupabase]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle up/down arrows for command history
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length === 0) return;

      if (historyIndex === -1) {
        // Starting to browse history, save current input
        setTempInput(inputValue);
        setHistoryIndex(commandHistory.length - 1);
        setInputValue(commandHistory[commandHistory.length - 1]);
      } else if (historyIndex > 0) {
        // Go further back in history
        setHistoryIndex(historyIndex - 1);
        setInputValue(commandHistory[historyIndex - 1]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex === -1) return;

      if (historyIndex < commandHistory.length - 1) {
        // Go forward in history
        setHistoryIndex(historyIndex + 1);
        setInputValue(commandHistory[historyIndex + 1]);
      } else {
        // Back to current input
        setHistoryIndex(-1);
        setInputValue(tempInput);
      }
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputValue.trim()) return;

    // Add to command history (avoid duplicates of last command)
    const trimmedInput = inputValue.trim();
    if (commandHistory[commandHistory.length - 1] !== trimmedInput) {
      setCommandHistory(prev => [...prev.slice(-49), trimmedInput]); // Keep last 50
    }
    setHistoryIndex(-1);
    setTempInput('');

    // Help command
    if (inputValue === '/help' || inputValue === '/?') {
      addSystemMessage(
        '**Commands:**\n' +
        '/roll <dice> — Roll dice (e.g. /roll 1d20+5, /roll 2d6 adv)\n' +
        '/spell <name> — Look up a spell (e.g. /spell fireball)\n' +
        '/pin [type] Name — Drop a pin at party location (e.g. /pin quest Shrine of Ubtao)\n' +
        '/encounter <type> — Start a test encounter (e.g. /encounter goblin)\n' +
        '/reset — Clear board (mobs, initiative, encounter)\n' +
        '/feature <text> — Request a feature (GM will review)\n' +
        '/help — Show this message'
      );
      setInputValue('');
      return;
    }

    // Reset board command
    if (inputValue === '/reset' || inputValue === '/clear') {
      const { useEncounterStore } = await import('../stores/encounterStore');
      const { useInitiativeStore } = await import('../stores/initiativeStore');
      useEncounterStore.getState().endEncounter();
      useInitiativeStore.getState().clearAllEntries();
      await useMobStore.getState().removeAllMobs();
      addSystemMessage('Board cleared — mobs, initiative, and encounter wiped.');
      setInputValue('');
      return;
    }

    // Feature request command
    if (inputValue.startsWith('/feature ')) {
      const featureText = inputValue.slice(9).trim();
      if (featureText) {
        addMessage({
          sender: chatSender,
          message: `**Feature Request:** ${featureText}`,
          type: 'system',
          characterName: chatCharacterName,
        });
        addSystemMessage(`Feature request logged from ${chatSender}. GM will review.`);
      } else {
        addSystemMessage('Usage: /feature <description of what you want>');
      }
      setInputValue('');
      return;
    }

    // Check if it's an encounter command
    if (inputValue.startsWith('/encounter')) {
      const encounterType = inputValue.slice(10).trim().toLowerCase() || 'goblin';
      const encounter = TEST_ENCOUNTERS[encounterType];

      if (!encounter) {
        const available = Object.keys(TEST_ENCOUNTERS).join(', ');
        addSystemMessage(`Unknown encounter. Available: ${available}`);
      } else if (currentCampaignId) {
        // Clear previous encounter mobs
        await removeAllMobs();
        clearInitiative();

        // Spawn all mobs through unified pipeline (DB-backed)
        const sceneId = useCombatStore.getState().currentSceneId ??
          useEncounterStore.getState().activeEncounter?.sceneId ?? undefined;
        for (const mobData of encounter.mobs) {
          await useMobStore.getState().spawnMob({
            campaignId: currentCampaignId,
            sceneId,
            name: mobData.name,
            ac: mobData.ac,
            maxHp: mobData.maxHp,
            abilities: {
              str: mobData.str, dex: mobData.dex, con: mobData.con,
              int: mobData.int, wis: mobData.wis, cha: mobData.cha,
            },
            actions: mobData.actions.map(a => ({
              name: a.name,
              attackBonus: a.attackBonus,
              damageDice: a.damageDice,
              damageType: a.damageType,
              range: 'melee' as const,
            })),
          }, {
            position: { x: 300, y: 200 },
          });
        }

        addSystemMessage(`Encounter: ${encounter.name}! ${encounter.mobs.length} enemies added.`);
      }
      setInputValue('');
      return;
    }

    // Check if it's a spell lookup command
    if (inputValue.startsWith('/spell ')) {
      const spellQuery = inputValue.slice(7).trim();
      if (!spellQuery) {
        addSystemMessage('Usage: /spell <spell name>');
        setInputValue('');
        return;
      }

      setIsSearching(true);
      setInputValue('');

      try {
        // Use Open5e's server-side search across ALL documents (not just SRD)
        // This finds XGtE, TCoE, etc. spells like Infestation, Toll the Dead, etc.
        const matchedSpells = await getSpells({ search: spellQuery, documentSlug: null });
        const queryLower = spellQuery.toLowerCase();

        // Find exact match first, then partial matches
        const exactMatch = matchedSpells.find(s => s.name.toLowerCase() === queryLower);
        const partialMatches = matchedSpells.filter(s =>
          s.name.toLowerCase().includes(queryLower)
        );

        // Helpers to build spellDamage/spellHealing from parsed spell desc
        const buildDamage = (spell: { name: string; desc: string }) => {
          const dmg = parseSpellDamage(spell.desc);
          return dmg ? { dice: dmg.dice, damageType: dmg.damageType, spellName: spell.name } : undefined;
        };
        const buildHealing = (spell: { name: string; desc: string }) => {
          const heal = parseSpellHealing(spell.desc);
          return heal ? { dice: heal.dice, spellName: spell.name, abilityMod: 0 } : undefined;
        };

        if (exactMatch) {
          addAndSyncSpellLookup({ sender: chatSender, characterName: chatCharacterName, spell: exactMatch, spellDamage: buildDamage(exactMatch), spellHealing: buildHealing(exactMatch) });
        } else if (partialMatches.length === 1) {
          addAndSyncSpellLookup({ sender: chatSender, characterName: chatCharacterName, spell: partialMatches[0], spellDamage: buildDamage(partialMatches[0]), spellHealing: buildHealing(partialMatches[0]) });
        } else if (partialMatches.length > 1) {
          const suggestions = partialMatches.slice(0, 5).map(s => s.name).join(', ');
          addSystemMessage(`Multiple spells found: ${suggestions}${partialMatches.length > 5 ? '...' : ''}`);
        } else {
          addSystemMessage(`No spell found matching "${spellQuery}"`);
        }
      } catch (err) {
        addSystemMessage(`Error looking up spell: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setIsSearching(false);
      }
      return;
    }

    // Check if it's a pin command (GM only — drops a pin at party location)
    if (inputValue.startsWith('/pin ') || inputValue === '/pin') {
      const args = inputValue.slice(5).trim();
      if (!args) {
        addSystemMessage('Usage: /pin [type] Name — Description\nTypes: town, city, village, dungeon, cave, quest, encounter, landmark, shop, tavern, temple, camp, ruins, portal');
        setInputValue('');
        return;
      }

      // Get party location from campaign map store
      const cmState = useCampaignMapStore.getState();
      const partyLoc = cmState.partyLocation;
      const campaignMap = cmState.activeCampaignMap;

      if (!campaignMap) {
        addSystemMessage('No campaign map loaded. Switch to campaign map mode first.');
        setInputValue('');
        return;
      }

      // Resolve party position (from pin or freeform x/y)
      let pinX: number | undefined;
      let pinY: number | undefined;
      if (partyLoc?.x !== undefined && partyLoc?.y !== undefined) {
        pinX = partyLoc.x;
        pinY = partyLoc.y;
      } else if (partyLoc?.currentPinId) {
        const atPin = cmState.pins.get(partyLoc.currentPinId);
        if (atPin) { pinX = atPin.x; pinY = atPin.y; }
      }

      if (pinX === undefined || pinY === undefined) {
        addSystemMessage('Drag the party marker to a location on the campaign map first.');
        setInputValue('');
        return;
      }

      // Parse: optional type keyword, then name, optional " — description"
      const PIN_TYPES = ['town','city','village','dungeon','cave','quest','encounter','landmark','shop','tavern','temple','camp','ruins','portal','custom'] as const;
      const words = args.split(/\s+/);
      let iconType: typeof PIN_TYPES[number] = 'landmark';
      let rest = args;

      if (PIN_TYPES.includes(words[0].toLowerCase() as typeof PIN_TYPES[number])) {
        iconType = words[0].toLowerCase() as typeof PIN_TYPES[number];
        rest = words.slice(1).join(' ');
      }

      // Split on em-dash or double-hyphen for description
      const dashIdx = rest.search(/\s[—–]\s|--/);
      let pinName = rest;
      let description: string | undefined;
      if (dashIdx >= 0) {
        pinName = rest.slice(0, dashIdx).trim();
        description = rest.slice(dashIdx).replace(/^[\s—–-]+/, '').trim();
      }

      if (!pinName) {
        addSystemMessage('Pin name is required. Usage: /pin [type] Name — Description');
        setInputValue('');
        return;
      }

      setInputValue('');

      try {
        const newPin = await cmState.addPin({
          campaignMapId: campaignMap.id,
          x: pinX,
          y: pinY,
          name: pinName,
          iconType,
          state: 'discovered',
          description,
          tags: [],
        });

        if (newPin) {
          addSystemMessage(`Pinned "${pinName}" (${iconType}) at party location.`);
          // Select the new pin
          cmState.selectPin(newPin.id);
          // Move party to the new pin
          await cmState.setPartyLocation(newPin.id);
        }
      } catch (err) {
        addSystemMessage(`Failed to create pin: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
      return;
    }

    // Check module command registry (fallback for unrecognized slash commands)
    if (inputValue.startsWith('/')) {
      const spaceIdx = inputValue.indexOf(' ');
      const commandName = (spaceIdx === -1 ? inputValue.slice(1) : inputValue.slice(1, spaceIdx)).toLowerCase();
      const moduleCmd = getCommand(commandName);
      if (moduleCmd) {
        const args = spaceIdx === -1 ? '' : inputValue.slice(spaceIdx + 1).trim();
        setInputValue('');
        try {
          const result = await moduleCmd.handler({
            campaignId: currentCampaignId || '',
            userId: userId || '',
            isGM: isGM,
            args,
            parsedArgs: args ? args.split(/\s+/) : [],
          });
          if (result) {
            addSystemMessage(result);
          }
        } catch (err) {
          addSystemMessage(`Command /${commandName} failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
        return;
      }
    }

    // Check if it's a dice roll command
    if (inputValue.startsWith('/roll ')) {
      let diceExpression = inputValue.slice(6).trim();

      // Check for advantage/disadvantage modifiers FIRST
      let rollType: 'normal' | 'advantage' | 'disadvantage' = 'normal';
      if (diceExpression.endsWith(' adv') || diceExpression.endsWith(' advantage')) {
        rollType = 'advantage';
        diceExpression = diceExpression.replace(/\s+(adv|advantage)$/, '').trim();
      } else if (diceExpression.endsWith(' dis') || diceExpression.endsWith(' disadvantage')) {
        rollType = 'disadvantage';
        diceExpression = diceExpression.replace(/\s+(dis|disadvantage)$/, '').trim();
      }

      // Check for roll label keyword (e.g., /roll init 1d20+2, /roll attack 1d20+5)
      let rollLabel: RollLabel = null;
      const parts = diceExpression.split(/\s+/);
      if (parts.length >= 2) {
        // Only check for label if there are at least 2 parts (label + dice)
        const firstWord = parts[0].toLowerCase();
        const labelInfo = parseRollLabel(firstWord);
        if (labelInfo) {
          rollLabel = labelInfo.label;
          // Remove the label from the expression
          diceExpression = parts.slice(1).join(' ').trim();
        }
      }

      // Support shorthand: /roll 20 → /roll 1d20, /roll 20 adv → /roll 1d20 adv
      if (/^\d+$/.test(diceExpression)) {
        diceExpression = `1d${diceExpression}`;
      }

      if (!diceExpression) {
        addSystemMessage('Usage: /roll 20, /roll 1d20, /roll 2d6+3, /roll 1d20 adv, /roll 1d20 dis');
      } else if (!isValidDiceExpression(diceExpression)) {
        addSystemMessage(`Invalid dice expression "${diceExpression}". Try: 1d20, 2d6+3, 1d8-1`);
      } else if (!isReady) {
        addSystemMessage('Dice roller is loading...');
      } else {
        setInputValue('');

        // For advantage/disadvantage, we need to roll twice and pick high/low
        if (rollType !== 'normal' && diceExpression.match(/^\d*d20$/i)) {
          // Roll twice for d20 adv/dis
          const result1 = await roll(diceExpression);
          const result2 = await roll(diceExpression);

          if (result1 && result2) {
            const val1 = result1.total;
            const val2 = result2.total;
            const chosen = rollType === 'advantage'
              ? Math.max(val1, val2)
              : Math.min(val1, val2);
            const discarded = rollType === 'advantage'
              ? Math.min(val1, val2)
              : Math.max(val1, val2);

            // Create a combined result showing both rolls
            const combinedResult = {
              notation: `${diceExpression} (${rollType})`,
              rolls: [
                ...result1.rolls.map(r => ({ ...r, value: val1 === chosen ? r.value : r.value })),
              ],
              total: chosen,
              // Extra metadata for display
              advDisRolls: [val1, val2],
              discardedValue: discarded,
            };

            addAndSyncDiceRoll({
              sender: chatSender,
              characterName: chatCharacterName,
              notation: diceExpression,
              result: combinedResult as any,
              rollType,
              rollLabel,
            });
          }
        } else {
          // Normal roll
          const result = await roll(diceExpression);
          if (result) {
            addAndSyncDiceRoll({
              sender: chatSender,
              characterName: chatCharacterName,
              notation: diceExpression,
              result,
              rollType: 'normal',
              rollLabel,
            });
          }
        }
        return;
      }
    } else if (speakingAsNpc) {
      // Send as NPC
      addNpcMessage({
        npcId: speakingAsNpc.id,
        npcName: speakingAsNpc.name,
        npcTitle: speakingAsNpc.title,
        factionColor: speakingAsNpc.factionId
          ? getFactionColorForChat(speakingAsNpc.factionId)
          : undefined,
        message: inputValue,
      });
    } else {
      addAndSyncMessage({
        sender: chatSender,
        characterName: chatCharacterName,
        message: inputValue,
        type: 'player',
      });

      // In solo mode, route player messages to the AI DM
      const soloState = useSoloGamemasterStore.getState();
      if (soloState.isSoloMode) {
        soloState.handlePlayerInput(inputValue);
      }
    }

    setInputValue('');
  };

  // Handle choice button clicks from AI DM
  const handleChoiceAction = (action: string) => {
    if (action === 'create_character') {
      setShowCharCreation(true);
      return;
    }
    // All other actions: send as player input to AI DM
    const soloState = useSoloGamemasterStore.getState();
    if (soloState.isSoloMode) {
      addMessage({
        sender: chatSender,
        message: action,
        type: 'player',
        characterName: chatCharacterName,
      });
      soloState.handlePlayerInput(action);
    }
  };

  const getMessageColor = (msg: ChatMessage) => {
    switch (msg.type) {
      case 'dm':
        return 'var(--color-dm-chat)';
      case 'ai_dm':
        return '#d4af37';
      case 'system':
        return 'var(--color-text-muted)';
      case 'dice':
        return 'var(--color-accent)';
      case 'npc':
        return msg.factionColor || '#d4af37';
      case 'roll_request':
        return '#cc5de8';
      case 'ai_dm_choices':
        return '#d4af37';
      default:
        return 'var(--color-text)';
    }
  };

  // Colors and icons for different roll types
  const getRollLabelColor = (label: RollLabel): string => {
    switch (label) {
      case 'initiative': return '#4dabf7'; // blue
      case 'attack': return '#ff6b6b'; // red
      case 'crit': return '#ffd700'; // gold - critical hit!
      case 'damage': return '#ff922b'; // orange
      case 'crit-damage': return '#ff4500'; // bright red-orange - crit damage!
      case 'save': return '#51cf66'; // green
      case 'check': return '#cc5de8'; // purple
      case 'heal': return '#20c997'; // teal
      case 'death': return '#868e96'; // gray
      default: return 'var(--color-accent)';
    }
  };

  return (
    <>
    {/* Character creation wizard (solo mode) */}
    {showCharCreation && (
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}>
        <div style={{ maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
          <CharacterCreationWizard
            onClose={() => setShowCharCreation(false)}
            onCreated={() => {
              setShowCharCreation(false);
              // Re-initialize AI DM now that a character exists
              const soloState = useSoloGamemasterStore.getState();
              if (soloState.isSoloMode) {
                soloState.initialize();
              }
            }}
          />
        </div>
      </div>
    )}
    <div style={chatStyles.wrapper}>
      {/* Chat messages */}
      <div style={chatStyles.messageList}>
        {/* Loading state — fantasy wave animation */}
        {isChatLoading && messages.length === 0 && (
          <div style={chatStyles.loadingWave}>
            {/* Animated wave SVG */}
            <svg width="120" height="40" viewBox="0 0 120 40" style={{ overflow: 'visible' }}>
              <defs>
                <linearGradient id="waveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#d4af37" stopOpacity="0.3" />
                  <stop offset="50%" stopColor="#d4af37" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#d4af37" stopOpacity="0.3" />
                </linearGradient>
              </defs>
              <path
                d="M0,20 Q15,5 30,20 T60,20 T90,20 T120,20"
                fill="none"
                stroke="url(#waveGrad)"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <animate
                  attributeName="d"
                  dur="2s"
                  repeatCount="indefinite"
                  values="
                    M0,20 Q15,5 30,20 T60,20 T90,20 T120,20;
                    M0,20 Q15,35 30,20 T60,20 T90,20 T120,20;
                    M0,20 Q15,5 30,20 T60,20 T90,20 T120,20
                  "
                />
              </path>
              <path
                d="M0,25 Q15,10 30,25 T60,25 T90,25 T120,25"
                fill="none"
                stroke="url(#waveGrad)"
                strokeWidth="1.5"
                strokeLinecap="round"
                opacity="0.5"
              >
                <animate
                  attributeName="d"
                  dur="2.5s"
                  repeatCount="indefinite"
                  values="
                    M0,25 Q15,40 30,25 T60,25 T90,25 T120,25;
                    M0,25 Q15,10 30,25 T60,25 T90,25 T120,25;
                    M0,25 Q15,40 30,25 T60,25 T90,25 T120,25
                  "
                />
              </path>
            </svg>
            <span style={chatStyles.loadingText}>
              The tides are turning...
            </span>
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={chatStyles.messageBubble}
          >
            <div style={chatStyles.senderRow}>
              {/* NPC messages get special sender treatment */}
              {msg.type === 'npc' ? (
                <span style={{ ...chatStyles.npcSender, color: msg.factionColor || '#d4af37' }}>
                  {msg.npcName}
                  {msg.npcTitle && (
                    <span style={chatStyles.npcTitle}>
                      {msg.npcTitle}
                    </span>
                  )}
                </span>
              ) : msg.type === 'ai_dm' || msg.type === 'ai_dm_choices' ? (
                <span style={chatStyles.aiDmSender}>
                  <Icon name="sparkle" size={12} color="#d4af37" />
                  Dungeon Master
                </span>
              ) : (
                <span style={{
                  fontWeight: 'var(--font-weight-bold)',
                  color: getMessageColor(msg),
                }}>
                  {msg.characterName || msg.sender}
                </span>
              )}
              <span style={chatStyles.timestamp}>
                {msg.timestamp.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            {msg.diceResult ? (
              <DiceResultDisplay
                result={msg.diceResult}
                rollType={msg.rollType}
                rollLabel={msg.rollLabel ? (ROLL_LABELS[msg.rollLabel as keyof typeof ROLL_LABELS]?.display || msg.rollLabel) : undefined}
                rollLabelColor={msg.rollLabel ? getRollLabelColor(msg.rollLabel) : undefined}
              />
            ) : msg.spellData ? (
              <div style={chatStyles.spellSection}>
                <SpellCardFull spell={msg.spellData} />
                {msg.spellDamage && (
                  <SpellDamageButton spellDamage={msg.spellDamage} sender={msg.sender} />
                )}
                {msg.spellHealing && (
                  <SpellHealingButton spellHealing={msg.spellHealing} sender={msg.sender} />
                )}
              </div>
            ) : msg.savePrompt ? (
              <SavePromptCard savePrompt={msg.savePrompt} />
            ) : msg.type === 'npc' ? (
              <NpcMessageBubble msg={msg} />
            ) : msg.type === 'ai_dm' || msg.type === 'ai_dm_choices' ? (
              <AiDmMessageBubble msg={msg} onAction={handleChoiceAction} />
            ) : msg.type === 'roll_request' ? (
              <RollRequestCard msg={msg} />
            ) : msg.isoSpritePrompt ? (
              <IsoSpritePromptCard />
            ) : (
              <div style={{
                color: msg.type === 'dice'
                  ? 'var(--color-accent)'
                  : 'var(--color-text-secondary)',
              }}>
                {formatChatText(msg.message)}
              </div>
            )}
          </div>
        ))}
        {/* AI DM thinking indicator */}
        {isSoloMode && isAiProcessing && (
          <div style={chatStyles.aiThinking}>
            <Icon name="sparkle" size={14} />
            The Dungeon Master is thinking...
          </div>
        )}

        {/* AI DM error */}
        {isSoloMode && aiError && (
          <div style={chatStyles.aiError}>
            AI DM error: {aiError}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <form
        onSubmit={handleSendMessage}
        style={chatStyles.inputArea}
      >
        {/* Dialogue hints when speaking as NPC */}
        {speakingAsNpc && (
          <DialogueHintChips
            npc={speakingAsNpc}
            onSelect={(hint) => {
              setInputValue(hint);
              inputRef.current?.focus();
            }}
          />
        )}

        <div style={chatStyles.inputRow}>
          {/* NPC speaker indicator — shown only when speaking as NPC (set from GM Tray) */}
          {isGM && speakingAsNpc && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              backgroundColor: 'rgba(212, 175, 55, 0.12)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              borderRadius: 3,
              fontSize: 11,
              whiteSpace: 'nowrap',
            }}>
              <Icon name="crown" size={10} color="#d4af37" />
              <span style={{
                color: '#d4af37',
                fontFamily: 'Georgia, serif',
                fontWeight: 600,
              }}>
                {speakingAsNpc.name}
              </span>
              <button
                onClick={() => setSpeakingAsNpc(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0 2px',
                  color: 'rgba(192, 192, 192, 0.6)',
                  fontSize: 14,
                  lineHeight: 1,
                }}
                title="Stop speaking as NPC"
              >
                ×
              </button>
            </div>
          )}

          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isSearching
                ? 'Looking up spell...'
                : isAiProcessing
                ? 'Waiting for the DM...'
                : speakingAsNpc
                ? `Speak as ${speakingAsNpc.name}...`
                : isSoloMode
                ? 'What do you do? (or /roll, /spell...)'
                : 'Type a message, /roll 20, or /spell fireball...'
            }
            disabled={isSearching || isAiProcessing}
            style={{
              flex: 1,
              padding: 'var(--spacing-sm)',
              fontSize: 'var(--font-size-sm)',
              backgroundColor: 'var(--color-surface)',
              color: speakingAsNpc ? '#d4af37' : 'var(--color-text)',
              border: `var(--border-width-thin) solid ${speakingAsNpc ? 'rgba(212, 175, 55, 0.3)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-md)',
              opacity: isSearching ? 0.7 : 1,
              fontFamily: speakingAsNpc ? 'Georgia, serif' : 'inherit',
              fontStyle: speakingAsNpc ? 'italic' : 'normal',
            }}
          />
        </div>
        <div style={{
          marginTop: 'var(--spacing-xs)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
        }}>
          {speakingAsNpc
            ? `Speaking as ${speakingAsNpc.name} · click × to stop`
            : isSoloMode
            ? 'Tell the AI DM what you do · /roll 20 · /spell fireball'
            : '/roll 20 · /spell fireball · /pin quest Shrine'
          }
        </div>
      </form>
    </div>
    </>
  );
}
